import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Instagram,
  Facebook,
  Phone,
  MapPin,
  ChevronUp,
  Building2,
  Youtube,
  Linkedin,
  MessageSquare,
  Award,
  Users,
  ShieldCheck,
  Clock,
  User,
  CheckCircle2,
  BadgeCheck,
} from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PageLayout from "@/components/PageLayout";
import Footer from "@/components/Footer";
import FloatingContactBar from "@/components/FloatingContactBar";

const XLogo = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932 6.064-6.932zm-1.292 19.494h2.039L6.486 3.24H4.298l13.311 17.407z" />
  </svg>
);

const TikTokLogo = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.525.02c1.31.036 2.612.351 3.846.917a6.837 6.837 0 0 1-2.156 5.015 6.692 6.692 0 0 1-4.907 1.464V3.122a3.391 3.391 0 0 0 1.626-.639 3.37 3.37 0 0 0 1.591-2.463V0l.001.02zM9.154 12.156c0 1.637-1.326 2.963-2.963 2.963a2.963 2.963 0 1 1 0-5.926c.118 0 .235.007.351.021V6.115a6.046 6.046 0 0 0-5.467 4.51 6.05 6.05 0 0 0 4.131 7.155 6.05 6.05 0 0 0 7.035-3.624 5.92 5.92 0 0 0 .317-1.921V0h-3.4v12.156z" />
  </svg>
);

const offices = [
  {
    city: "Dubai",
    type: "Global HQ",
    address: "Techno Park, National Industries Park, Jebel Ali",
    phone: "+971 4 347 0111",
    tag: "Primary Hub",
  },
  {
    city: "Abu Dhabi",
    type: "Regional Office",
    address: "ICAD-1, Mussafah Industrial Area",
    phone: "+971 2 555 2131",
    tag: "Operations",
  },
  {
    city: "Riyadh",
    type: "KSA Branch",
    address: "Abdulrahman Al Rosis Centre, Al Olaya Street",
    phone: "+966 11 465 2211",
    tag: "Expansion",
  },
];

const trustItems = [
  { icon: Award, title: "30+", sub: "Years Experience" },
  { icon: Users, title: "8000+", sub: "Projects Delivered" },
  { icon: ShieldCheck, title: "UAE & KSA", sub: "Regional Coverage" },
  { icon: Clock, title: "Fast", sub: "Response & Support" },
];

const socialLinks = [
  { icon: Instagram, label: "Instagram", href: "#" },
  { icon: Facebook, label: "Facebook", href: "#" },
  { icon: TikTokLogo, label: "TikTok", href: "#" },
  { icon: Youtube, label: "YouTube", href: "#" },
  { icon: XLogo, label: "X", href: "#" },
  { icon: Linkedin, label: "LinkedIn", href: "#" },
];

const MiniQuoteForm = ({ quoteRef }: { quoteRef: React.RefObject<HTMLDivElement> }) => {
  const navigate = useNavigate();
  const [geo, setGeo] = useState({ code: "+971", country: "AE" });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    requirements: "",
  });

  useEffect(() => {
    const controller = new AbortController();

    const geoTimeout = setTimeout(() => {
      fetch("https://ipapi.co/json/", { signal: controller.signal })
        .then((res) => res.json())
        .then((data) => {
          if (data.country_calling_code) {
            const formattedCode = data.country_calling_code.startsWith("+")
              ? data.country_calling_code
              : `+${data.country_calling_code}`;

            setGeo({
              code: formattedCode,
              country: data.country_code || "AE",
            });
          }
        })
        .catch(() => {});
    }, 3000);

    return () => {
      controller.abort();
      clearTimeout(geoTimeout);
    };
  }, []);

  const handleMiniSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    navigate("/thankyou");

    const SCRIPT_URL =
      "https://script.google.com/macros/s/AKfycby5HKEZNMObZRaeTidKlOMCLR_2YpgZuP2jOscP-OHWz2pi8PT0j8giTglCwm0VO5_J/exec";

    fetch(SCRIPT_URL, {
      method: "POST",
      mode: "no-cors",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...formData,
        phone: `${geo.code} ${formData.phone}`,
        source: "Info Page",
      }),
    }).catch(() => {});
  };

  return (
    <div
      ref={quoteRef}
      className="relative w-full rounded-2xl bg-[#0c0c0c] shadow-none md:shadow-[0_30px_80px_rgba(0,0,0,0.55)] border-0 md:border md:border-purple-500/20 p-6 md:p-8 overflow-hidden scroll-mt-24"
    >
      <div className="hidden md:block absolute top-0 right-0 w-72 h-72 bg-purple-600/10 blur-[100px] rounded-full pointer-events-none" />

      <div className="relative z-10">
        <div className="border-b border-white/10 pb-5 mb-5">
          <h2 className="text-xl font-black text-white uppercase tracking-tight">
            Quick Inquiry
          </h2>
          <p className="text-[9px] font-black text-[#bd6aff] uppercase tracking-[0.24em] mt-2">
            Direct contact with BA Tents team
          </p>
        </div>

        <form className="space-y-4" onSubmit={handleMiniSubmit}>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="text"
              placeholder="Full Name"
              required
              className="w-full bg-[#0d0d0d] border border-zinc-800 rounded-xl pl-11 pr-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="email"
              placeholder="Email Address"
              required
              className="w-full bg-[#0d0d0d] border border-zinc-800 rounded-xl pl-11 pr-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>

          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-purple-400 text-[10px] font-black border-r border-zinc-800 pr-3">
              {geo.code}
            </div>
            <input
              type="tel"
              required
              placeholder="Mobile Number"
              className="w-full bg-[#0d0d0d] border border-zinc-800 rounded-xl pl-16 pr-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
          </div>

          <textarea
            placeholder="Tell us about your project requirements..."
            className="w-full h-24 md:h-28 bg-[#0d0d0d] border border-zinc-800 rounded-xl px-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none resize-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
            value={formData.requirements}
            onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
          />

          <button
            type="submit"
            className="w-full py-4 bg-gradient-to-r from-[#8b31ff] to-[#bd6aff] text-white font-black rounded-xl uppercase tracking-widest text-[11px] shadow-none md:shadow-[0_10px_30px_rgba(139,49,255,0.24)] active:scale-95 transition-all hover:brightness-110"
          >
            Send Request
          </button>
        </form>
      </div>
    </div>
  );
};

const Info = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const quoteRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = "Contact BA Tents | Offices in Dubai, Abu Dhabi & Riyadh";

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Contact BA Tents for tent rental, tent sale, event structures, pavilions, warehouse tents, and temporary installations across UAE and KSA."
      );
    }

    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    window.scrollTo(0, 0);

    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToQuote = useCallback(() => {
    quoteRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <PageLayout>
      <div className="min-h-screen bg-black text-white overflow-hidden w-screen max-w-none relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] -mt-28 md:-mt-12">
        <section className="relative pt-28 md:pt-32 pb-10 md:pb-16 bg-black">
          <div className="absolute inset-0 bg-black pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/40 to-black pointer-events-none" />

          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid lg:grid-cols-[1.35fr_0.85fr] gap-8 lg:gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/20 bg-white/10 mb-5">
                  <BadgeCheck className="w-3.5 h-3.5 text-[#bd6aff]" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-white">
                    Establishment & Contact
                  </span>
                </div>

                <h1 className="text-[36px] md:text-[64px] font-black text-white leading-[1.05] mb-5 tracking-tight uppercase">
                  Global Reach <br className="hidden md:block" />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#bd6aff] to-purple-400">
                    Local Presence
                  </span>
                </h1>

                <p className="text-zinc-300 text-sm md:text-base leading-relaxed max-w-2xl mb-7">
                  From our Dubai HQ to strategic regional offices across the GCC, BA Tents delivers premium tent structures, event solutions, and industrial temporary installations with fast support.
                </p>

                <div className="flex flex-row items-center gap-1.5 md:gap-3 max-w-xl mb-8">
                  <button
                    onClick={scrollToQuote}
                    className="flex-1 py-4 bg-gradient-to-r from-[#8b31ff] to-[#bd6aff] rounded-xl text-white font-black text-[10px] md:text-xs tracking-wider flex items-center justify-center gap-1.5 uppercase shadow-none md:shadow-[0_0_20px_rgba(139,49,255,0.3)] border-0 active:scale-95 transition-transform"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    <span>Get Quote</span>
                  </button>

                  <a
                    href="tel:+971588243660"
                    className="flex-1 py-4 bg-white rounded-xl text-black font-black text-[10px] md:text-xs flex items-center justify-center gap-1.5 uppercase border-0 shadow-none active:scale-95 transition-transform"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    <span>Call Now</span>
                  </a>

                  <a
                    href="https://wa.me/971588243660"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 py-4 bg-emerald-500 rounded-xl text-white font-black text-[10px] md:text-xs flex items-center justify-center gap-1.5 uppercase border-0 shadow-none active:scale-95 transition-transform"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    <span>WhatsApp</span>
                  </a>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl pt-5 border-t border-white/10">
                  {trustItems.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-left">
                      <div className="p-2 bg-purple-500/10 rounded-lg shrink-0">
                        <item.icon className="w-5 h-5 text-[#bd6aff]" />
                      </div>
                      <div>
                        <p className="text-white font-bold text-sm leading-none whitespace-nowrap">
                          {item.title}
                        </p>
                        <p className="text-zinc-300 text-[9px] uppercase tracking-wider font-bold mt-1.5">
                          {item.sub}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <MiniQuoteForm quoteRef={quoteRef} />
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-14">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-8 md:gap-10 items-start">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              className="rounded-2xl bg-[#080808] p-6 md:p-8"
            >
              <p className="text-[#bd6aff] text-[10px] font-black uppercase tracking-[0.32em] mb-3">
                Connect With Us
              </p>
              <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight text-white leading-[0.98] mb-5">
                Let's Build <br />
                <span className="text-[#bd6aff]">Your Vision.</span>
              </h2>
              <p className="text-zinc-400 text-sm leading-relaxed mb-7">
                Reach our sales and project team for rental, sale, installation, site inspection, and custom temporary structure planning.
              </p>

              <div className="grid sm:grid-cols-2 gap-3 mb-7">
                <a
                  href="mailto:info@batents.ae"
                  className="flex items-center gap-3 rounded-xl bg-white/[0.04] px-4 py-4 hover:bg-white/[0.07] transition-colors"
                >
                  <Mail className="w-4 h-4 text-[#bd6aff]" />
                  <span className="text-[10px] font-black tracking-widest uppercase text-zinc-300">
                    info@batents.ae
                  </span>
                </a>

                <a
                  href="tel:+971588243660"
                  className="flex items-center gap-3 rounded-xl bg-white/[0.04] px-4 py-4 hover:bg-white/[0.07] transition-colors"
                >
                  <Phone className="w-4 h-4 text-[#bd6aff]" />
                  <span className="text-[10px] font-black tracking-widest uppercase text-zinc-300">
                    +971 58 824 3660
                  </span>
                </a>
              </div>

              <div className="flex flex-wrap gap-2">
                {socialLinks.map((item, i) => (
                  <a
                    key={i}
                    href={item.href}
                    aria-label={item.label}
                    className="w-11 h-11 rounded-xl bg-white/[0.04] hover:bg-[#bd6aff] text-white flex items-center justify-center transition-all"
                  >
                    <item.icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              className="grid sm:grid-cols-2 gap-3"
            >
              {[
                "Tent Rental & Sale",
                "Event & Exhibition Structures",
                "Warehouse & Storage Tents",
                "Sports Tent Setup",
                "Pavilions & Trade Shows",
                "UAE & KSA Installation",
              ].map((item) => (
                <div key={item} className="flex items-center gap-3 rounded-xl bg-[#080808] px-4 py-4">
                  <CheckCircle2 className="w-4 h-4 text-[#bd6aff] shrink-0" />
                  <span className="text-zinc-300 text-[11px] font-black uppercase tracking-wider">
                    {item}
                  </span>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-6 pb-20 md:pb-28">
          <div className="max-w-4xl mx-auto text-center mb-8 md:mb-12">
            <p className="text-[#bd6aff] text-[10px] font-black uppercase tracking-[0.32em] mb-3">
              Our Network
            </p>
            <h2 className="text-3xl md:text-6xl font-black uppercase tracking-tight text-white leading-[0.95] mb-5">
              Offices Across <span className="text-[#bd6aff]">UAE & KSA</span>
            </h2>
            <p className="text-zinc-400 text-sm md:text-base leading-relaxed max-w-3xl mx-auto">
              Regional presence for faster support, project coordination, and reliable installation services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            {offices.map((office, idx) => (
              <motion.div
                key={office.city}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.08 }}
                viewport={{ once: true, margin: "-80px" }}
                className="group relative p-6 md:p-7 rounded-2xl bg-[#080808] hover:bg-[#0d0d0d] transition-colors duration-300"
              >
                <div className="flex justify-between items-start mb-9">
                  <div className="p-3 rounded-xl bg-white/[0.04] group-hover:bg-[#bd6aff] transition-all">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-zinc-600">
                    {office.tag}
                  </span>
                </div>

                <h3 className="text-3xl font-black mb-2 uppercase tracking-tight text-white">
                  {office.city}
                </h3>
                <p className="text-[#bd6aff] text-[10px] font-black uppercase tracking-widest mb-6">
                  {office.type}
                </p>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-zinc-500 shrink-0 mt-1" />
                    <p className="text-zinc-400 text-sm leading-snug">
                      {office.address}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-zinc-500 shrink-0" />
                    <p className="text-zinc-400 text-sm">{office.phone}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      </div>

      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            onClick={scrollToTop}
            aria-label="Back to top"
            className="hidden md:flex fixed bottom-24 right-8 z-[100] group flex-col items-center gap-3"
          >
            <div className="relative w-[2px] h-16 overflow-hidden bg-white/10 rounded-full">
              <motion.div
                animate={{ y: [64, -64], opacity: [0, 1, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute inset-0 w-full h-1/2 bg-gradient-to-t from-transparent via-white to-transparent"
              />
            </div>

            <div className="w-12 h-12 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm flex items-center justify-center group-hover:bg-white group-hover:scale-110 transition-all duration-500 shadow-2xl">
              <ChevronUp className="w-6 h-6 text-white group-hover:text-black transition-colors duration-300 stroke-[3px]" />
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      <FloatingContactBar onQuoteClick={scrollToQuote} />
      <Footer />
    </PageLayout>
  );
};

export default Info;
